import { useState } from "react";
import { useResetPassword } from "ra-supabase-core";
import { Form, required, useNotify, useRedirect, useTranslate } from "ra-core";
import type { FieldValues, SubmitHandler } from "react-hook-form";
import { TextInput } from "@/components/admin/text-input";
import { Button } from "@/components/ui/button";
import {
  AuthShell,
  authButtonClassName,
  authInputClassName,
} from "@/components/atomic-crm/login/AuthShell";

interface FormData {
  email: string;
}

export const ForgotPasswordPage = () => {
  const [loading, setLoading] = useState(false);

  const notify = useNotify();
  const redirect = useRedirect();
  const translate = useTranslate();
  const [, { mutateAsync: resetPassword }] = useResetPassword({
    onSuccess: () => {
      redirect("/login?passwordRecoveryEmailSent=1");
    },
    onError: () => undefined,
  });

  const submit = async (values: FormData) => {
    try {
      setLoading(true);
      await resetPassword({
        email: values.email,
      });
    } catch {
      notify("crm.auth.generic_error", {
        type: "warning",
        messageArgs: {
          _: "Something went wrong. Please try again.",
        },
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell>
      <div className="space-y-6">
        <div>
          <h2 className="font-heading text-2xl font-extrabold tracking-tight">
            {translate("ra-supabase.reset_password.forgot_password", {
              _: "Forgot password?",
            })}
          </h2>
          <p className="mt-1 text-[13px] text-muted-foreground">
            {translate("ra-supabase.reset_password.forgot_password_details", {
              _: "Enter your email to receive a reset password link.",
            })}
          </p>
        </div>
        <Form<FormData>
          className="space-y-4"
          onSubmit={submit as SubmitHandler<FieldValues>}
        >
          <TextInput
            source="email"
            label={translate("ra.auth.email", {
              _: "Email",
            })}
            autoComplete="email"
            inputClassName={authInputClassName}
            validate={required()}
          />
          <Button
            type="submit"
            className={authButtonClassName}
            disabled={loading}
          >
            {translate("crm.action.reset_password", {
              _: "Reset password",
            })}
          </Button>
        </Form>
      </div>
    </AuthShell>
  );
};

ForgotPasswordPage.path = "forgot-password";
